
import React, { useRef } from "react";
import { FileText, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface FileUploadProps {
  file: File | null;
  error?: string;
  onFileChange: (file: File | null) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({ file, error, onFileChange }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onFileChange(e.target.files[0]);
    }
  };
  
  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onFileChange(e.dataTransfer.files[0]);
    }
  };
  
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  const handleFileRemove = () => {
    onFileChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  
  return (
    <div className="space-y-2">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        accept=".js,.ts,.json,.py,.txt,.md,.zip"
      />
      
      <div 
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors hover:border-primary/50 ${
          file ? "bg-primary/5 border-primary/20" : error ? "border-destructive/30 bg-destructive/5" : ""
        }`}
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleFileDrop}
        onDragOver={handleDragOver}
      >
        {!file ? (
          <div className="flex flex-col items-center py-4">
            <Upload className="h-12 w-12 text-muted-foreground mb-2" />
            <p className="text-lg font-medium">
              Drag your agent file here or click to browse
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Supports Python (.py), JavaScript (.js), TypeScript (.ts), JSON (.json), Text (.txt), Markdown (.md), ZIP (.zip)
            </p>
          </div>
        ) : (
          <div className="flex items-center justify-between bg-background rounded-md p-3">
            <div className="flex items-center">
              <FileText className="h-8 w-8 text-primary mr-3" />
              <div>
                <p className="font-medium text-left">{file.name}</p>
                <p className="text-sm text-muted-foreground text-left">
                  {(file.size / 1024).toFixed(2)} KB
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                handleFileRemove();
              }}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        )}
      </div>
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  );
};

export default FileUpload;
